import { useState } from 'react'
import { Power, PowerOff } from 'lucide-react'
import type { components } from '@/api/schema'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatTimelineDate } from '@/components/timeline'

type AutomationChange = components['schemas']['AutomationChangeRead']

const COLLAPSED_COUNT = 3

function describe(change: AutomationChange, showScope: boolean) {
  const action = change.enabled ? 'Turned on' : 'Turned off'
  const subject = showScope ? (change.scope === 'project' ? 'Project' : 'PO') : null
  const text = subject ? `${subject} ${action.toLowerCase()}` : action
  return change.changed_by ? `${text} by ${change.changed_by}` : text
}

/**
 * Who turned automation on or off, and when — shown under an Automation switch.
 * `showScope` labels each row as a project or PO change (used on the PO card,
 * whose switch is also governed by the project).
 */
export function AutomationHistory({
  changes,
  isLoading,
  showScope = false,
  label = 'Automation history',
}: {
  changes: AutomationChange[] | undefined
  isLoading: boolean
  showScope?: boolean
  label?: string
}) {
  const [expanded, setExpanded] = useState(false)

  if (isLoading) {
    return (
      <div className="space-y-2 border-t pt-3" aria-hidden>
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    )
  }

  const rows = changes ?? []
  if (!rows.length) {
    return <p className="border-t pt-3 text-xs text-muted-foreground">No changes recorded</p>
  }

  const visible = expanded ? rows : rows.slice(0, COLLAPSED_COUNT)
  const hidden = rows.length - visible.length

  return (
    <div className="border-t pt-3">
      <ul className="space-y-2.5" aria-label={label}>
        {visible.map((change) => {
          const Icon = change.enabled ? Power : PowerOff
          return (
            <li key={change.uuid} className="flex items-start gap-2 text-xs">
              <Icon
                className={
                  change.enabled
                    ? 'mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground'
                    : 'mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground'
                }
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="break-words">{describe(change, showScope)}</p>
                <time dateTime={change.occurred_at} className="tabular-nums text-muted-foreground">
                  {formatTimelineDate(change.occurred_at)}
                </time>
              </div>
            </li>
          )
        })}
      </ul>
      {(hidden > 0 || expanded) && rows.length > COLLAPSED_COUNT && (
        <Button
          variant="link"
          size="sm"
          className="mt-1 h-auto px-0 text-xs text-muted-foreground"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `Show all ${rows.length}`}
        </Button>
      )}
    </div>
  )
}
